import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ParcelDto, ParcelListResponse, ParcelCentroidResponse } from '@forest/contracts';
import { api } from '@/lib/api-client';

type Props = {
  value: string | null;
  label: string | null;
  onPick: (opt: {
    id: string;
    label: string;
    latitude: number;
    longitude: number;
  }) => void;
  onClear: () => void;
  disabled?: boolean;
  /**
   * When true, picking a parcel should not auto-fill GPS (user already
   * edited lat/lon manually). The picker still reports the selection.
   */
  suppressCentroid?: boolean;
};

/**
 * Parcel picker (RQ-01) — searchable combobox over /api/parcels.
 *
 * On select, fetches /api/parcels/:id/centroid and passes lat/lon back
 * to the parent through onPick, unless suppressCentroid is true.
 */
export function ParcelPicker({
  value,
  label,
  onPick,
  onClear,
  disabled = false,
  suppressCentroid = false,
}: Props) {
  const { t } = useTranslation(['photos']);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [loadingPick, setLoadingPick] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const listQuery = useQuery<ParcelListResponse>({
    queryKey: ['parcels', 'picker', debounced],
    queryFn: async () => {
      const sp = new URLSearchParams();
      sp.set('page', '1');
      sp.set('limit', '20');
      if (debounced) sp.set('search', debounced);
      return api.get('parcels', { searchParams: sp }).json<ParcelListResponse>();
    },
    enabled: open,
    staleTime: 30_000,
  });

  const items = listQuery.data?.items ?? [];

  const handleSelect = async (p: ParcelDto) => {
    const optLabel = formatLabel(p);
    if (suppressCentroid) {
      // Caller opted out of GPS autofill; report NaN to signal "don't overwrite".
      onPick({ id: p.id, label: optLabel, latitude: Number.NaN, longitude: Number.NaN });
      setOpen(false);
      setQ('');
      return;
    }
    setLoadingPick(true);
    try {
      const c = await api
        .get(`parcels/${p.id}/centroid`)
        .json<ParcelCentroidResponse>();
      onPick({
        id: p.id,
        label: optLabel,
        latitude: c.latitude,
        longitude: c.longitude,
      });
    } catch {
      onPick({ id: p.id, label: optLabel, latitude: Number.NaN, longitude: Number.NaN });
    } finally {
      setLoadingPick(false);
      setOpen(false);
      setQ('');
    }
  };

  const displayLabel = useMemo(() => {
    if (!value) return '';
    return label ?? value;
  }, [value, label]);

  return (
    <div ref={containerRef} className="relative">
      {value ? (
        <div className="flex items-center gap-2 rounded-md border border-slate-300 bg-slate-50 px-2 py-1.5 text-sm">
          <span className="flex-1 truncate text-slate-800">{displayLabel}</span>
          {!disabled && (
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-red-600"
              onClick={onClear}
            >
              {t('photos:parcelPicker.clear', { defaultValue: 'Elimină' })}
            </button>
          )}
          {!disabled && (
            <button
              type="button"
              className="text-xs text-emerald-600 hover:text-emerald-700"
              onClick={() => setOpen(true)}
            >
              {t('photos:parcelPicker.change', { defaultValue: 'Schimbă' })}
            </button>
          )}
        </div>
      ) : (
        <input
          type="text"
          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          placeholder={t('photos:parcelPicker.placeholder', {
            defaultValue: 'Caută poligon (cod, UCF, raion)...',
          })}
          value={q}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            if (!open) setOpen(true);
          }}
        />
      )}

      {open && !disabled && !value && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {listQuery.isLoading || loadingPick ? (
            <div className="p-3 text-center text-xs text-slate-500">
              {t('photos:parcelPicker.loading', { defaultValue: 'Se încarcă...' })}
            </div>
          ) : items.length === 0 ? (
            <div className="p-3 text-center text-xs text-slate-400">
              {t('photos:parcelPicker.empty', {
                defaultValue: 'Niciun poligon găsit',
              })}
            </div>
          ) : (
            <ul className="py-1">
              {items.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-1.5 text-left text-xs hover:bg-emerald-50"
                    onClick={() => void handleSelect(p)}
                  >
                    <div className="font-medium text-slate-800">
                      {p.codProiect ?? '—'} · {p.ucf}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {p.raion} · {p.primarie}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {!disabled && value && !suppressCentroid && (
        <p className="mt-1 text-[10px] text-slate-400">
          {t('photos:parcelPicker.centroidHint', {
            defaultValue: 'GPS completat automat din centroidul poligonului.',
          })}
        </p>
      )}
    </div>
  );
}

function formatLabel(p: ParcelDto): string {
  return `${p.codProiect ?? '—'} · ${p.ucf} · ${p.raion}/${p.primarie}`;
}
